'use client'

import clsx from 'clsx'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import {
  FormEventHandler,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import BackButton from '../Button/BackButton'
import Button from '../Button/Button'
import CurrencySelector, {
  useCurrencySelector,
} from '../CurrencySelector/CurrencySelector'
import { CheckboxInputStandalone } from '../Form/CheckboxInput/CheckboxInput'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'
import Link, { LinkProps } from '../Link/Link'
import Notification, { NotificationProps } from '../Notification/Notification'
import { PromoCode } from '../PaymentDetails/partials/PromoCodeInput'
import PaymentOrderSummary, {
  PaymentOrderSummaryProps,
} from '../PaymentOrderSummary/PaymentOrderSummary'
// import SaveQuoteButton from '../SaveQuoteButton/SaveQuoteButton'
import ShipmentOrderSummary from '../ShipmentOrderSummary/ShipmentOrderSummary'
import Stepper, { StepperProps } from '../Stepper/Stepper'
import './safeScrollArea.css'

export interface OrderSummaryData {
  sms?: boolean
  terms?: boolean
}

export interface OrderSummaryProps extends StepperProps {
  /**
   * Optional total price of the order.
   */
  total?: number
  /**
   * Optional currency of the order.
   */
  currency?: Currency
  /**
   * Optional flag to check if SMS is checked.
   */
  smsChecked?: boolean
  /**
   * Optional flag to check if terms is checked.
   */
  terms?: boolean
  /**
   * Optional terms error message.
   */
  termsError?: string
  /**
   * Optional callback when terms change.
   */
  onTermsChange?: (checked: boolean) => void
  /**
   * Optional callback when terms error changes.
   */
  onTermsErrorChange?: (error: string) => void
  /**
   * Optional flag to disable the CTA button.
   */
  ctaDisabled?: boolean
  /**
   * Optional CTA label.
   */
  ctaLabel?: string
  /**
   * Optional mobile CTA label.
   */
  mobileCtaLabel?: string
  /**
   * Optional order details.
   */
  order?: Partial<Order>
  /**
   * Optional promo codes.
   */
  promoCodes?: PromoCode[]
  /**
   * Optional flag to check if quote is saved.
   */
  quoteSaved?: boolean
  /**
   * Flag to display loading icon on CTA button.
   */
  showLoadingIcon?: boolean
  /**
   * Flag to check if the order is international.
   */
  isInternational: boolean
  /**
   * Flag to check if the user is a guest.
   */
  isGuestUser?: boolean
  /**
   * Optional callback when clearing the cart.
   */
  onCartClearClick?: () => void
  /**
   * Optional callback to handle CTA click.
   */
  onCTAClick?: (data: OrderSummaryData) => void
  /**
   * Optional callback when removing a promo code.
   */
  onRemovePromoCode?: (promoCode: PromoCode) => void
  /**
   * Optional callback when saving a quote.
   */
  onQuoteSaveClick?: () => void
  /**
   * Optional additional class name to apply.
   */
  className?: string
  /**
   * Optional callback to convert price.
   */
  convertPrice?: PaymentOrderSummaryProps['convertPrice']
  /**
   * Cart ID.
   */
  cartId?: string
  /**
   * Optional flag to show no receipt checkbox.
   */
  showNoReceiptCheckbox?: boolean
  /**
   * Optional flag to show oversize item checkbox.
   */
  showOversizeItemCheckbox?: boolean
  /**
   * Optional revert changes link.
   */
  revertChanges?: LinkProps
  /**
   * Optional notification props.
   */
  notification?: NotificationProps
  /**
   * The brand name.
   */
  brand?: SubBrandName

  /**
   * Optional callback when checking the no receipt checkbox.
   */
  onNoReceiptChange?: (noReceipt: boolean) => void

  /*
   * No receipt checkbox state
   */
  noReceiptChecked?: boolean
  /*
   * Current pathname
   */
  pathName?: string
}

export default function OrderSummary({
  smsChecked = false,
  terms = false,
  termsError = '',
  onTermsChange,
  onTermsErrorChange,
  ctaDisabled = false,
  ctaLabel = 'Next',
  mobileCtaLabel = 'Next',
  order,
  promoCodes,
  // quoteSaved = false,
  isInternational = false,
  onCartClearClick,
  onCTAClick,
  onRemovePromoCode,
  // onQuoteSaveClick = () => {},
  isGuestUser,
  showLoadingIcon,
  className,
  convertPrice,
  cartId,
  showNoReceiptCheckbox,
  showOversizeItemCheckbox,
  revertChanges,
  notification,
  brand = 'shipplay',
  onNoReceiptChange,
  noReceiptChecked,
  pathName,
  ...stepperProps
}: OrderSummaryProps) {
  const [expanded, setExpanded] = useState(false)
  const [sms, setSMS] = useState(smsChecked ?? false)
  const [oversizeItem, setOversizeItem] = useState(false)
  const [orderData, setOrderData] = useState<Partial<Order> | undefined>(order)
  const [totalPriceFormatted, setTotalPriceFormatted] = useState('0')
  const selectedCurrency = useCurrencySelector((state) => state.selectedValue)
  const shouldLockScroll = expanded && !(isGuestUser && termsError)

  const onOversizeItemChange = useCallback((oversize: boolean) => {
    setOversizeItem(oversize)
  }, [])

  const stopPropagation = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()

      if (pathName === '/book/review' && isGuestUser && !terms) {
        onTermsErrorChange?.(
          'Please accept the Terms of Use and Privacy Policy to continue.',
        )
        setExpanded(true)
        return
      }

      if (showOversizeItemCheckbox && !oversizeItem) {
        alert('Please confirm oversize item agreement.')
        return
      }
      setExpanded(false)
      onCTAClick?.({ sms, terms })
    },
    [
      isGuestUser,
      onCTAClick,
      oversizeItem,
      pathName,
      showOversizeItemCheckbox,
      sms,
      terms,
      onTermsErrorChange,
    ],
  )

  const onComputedTotal = useCallback((totalPriceFormatted: string = '0') => {
    setTotalPriceFormatted(totalPriceFormatted)
  }, [])

  const subBrand = subBrands[brand]

  useEffect(() => setSMS(smsChecked), [smsChecked])

  useEffect(() => {
    if (order) setOrderData(order)
  }, [order])

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVh()
    window.addEventListener('resize', setVh)
    return () => window.removeEventListener('resize', setVh)
  }, [])

  return (
    <RemoveScroll enabled={shouldLockScroll}>
      <form onSubmit={onSubmit} className={className}>
        <div
          className={clsx(
            'bg-neutral-200',
            'sm-max:py-12 sm-max:px-8',
            'md-max:fixed top-0 left-0 md-max:w-screen md-max:h-screen md-max:transition-all md-max:py-16 md-max:px-8 md-max:z-30',
            'lg:px-8 lg:py-6 lg:rounded-8 overflow-y-auto lg:overflow-hidden',
            expanded
              ? `md-max:opacity-100 md-max:visible`
              : 'md-max:opacity-0 md-max:invisible',
          )}
        >
          <div className="md-max:hidden mb-4">
            <Stepper {...stepperProps} />
          </div>
          {notification && (
            <div className="mb-4">
              <Notification {...notification} />
            </div>
          )}
          <div
            className={clsx(
              'md-max:transition-transform md-max:transform-gpu',
              expanded ? `md-max:translate-y-0` : `md-max:translate-y-5`,
            )}
          >
            <div
              className={clsx(
                'flex justify-between flex-wrap gap-y-4 items-center @container',
              )}
            >
              <div className="align-top flex justify-between w-full md-max:hidden @[464px]:order-1">
                <div className="flex flex-col">
                  <Header as="h3" overrideClassName className="type-body-1">
                    Order Summary
                  </Header>
                  {cartId && (
                    <div aria-label="CartID" className="type-body-10">
                      Cart ID: {cartId}
                    </div>
                  )}
                </div>
                {onCartClearClick && (
                  <div>
                    <Button onClick={onCartClearClick} variant="tertiary">
                      Clear Cart
                    </Button>
                  </div>
                )}
              </div>
              <div className="lg:hidden flex items-center gap-x-2 type-body-3 @[464px]:order-2 w-full">
                <BackButton
                  onClick={() => setExpanded(false)}
                  className="!px-0 !border-0 type-body-3"
                />
                <div className="flex items-baseline justify-between w-full">
                  <div>
                    <Header as="h3" overrideClassName className="type-body-1">
                      Order summary
                    </Header>
                    {cartId && (
                      <div aria-label="CartID" className="type-body-10">
                        Cart ID: {cartId}
                      </div>
                    )}
                  </div>
                  {onCartClearClick && (
                    <div>
                      <Button onClick={onCartClearClick} variant="tertiary">
                        Clear cart
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* TODO: Uncomment when SaveQuoteButton is implemented
              <SaveQuoteButton
                saved={quoteSaved}
                onClick={onQuoteSaveClick}
                className="order-3 @[464px]:flex-1 justify-end max-w-max"
              /> */}
              {isInternational && (
                <div className="flex w-full @[464px]:w-[unset] order-3 @[464px]:order-2 @[464px]:ml-4">
                  <Icon
                    aria-label="InternationalIcon"
                    className="text-xs font-semibold bg-theme-accessibility text-white p-2 rounded-8"
                  >
                    International
                  </Icon>
                </div>
              )}
            </div>
            {/* Summaries */}
            <div className="flex flex-col gap-4 mt-4">
              <ShipmentOrderSummary legs={orderData?.legs || []} />
              <PaymentOrderSummary
                currentStep={stepperProps.current}
                eyebrow="Price Details"
                order={orderData}
                promoCodes={promoCodes}
                currency={selectedCurrency}
                onComputedTotal={onComputedTotal}
                onRemoveCode={(promoCode) => onRemovePromoCode?.(promoCode)}
                convertPrice={convertPrice}
              />
            </div>
            {stepperProps.current > 1 ? (
              <>
                {/* Checkboxes */}
                <div className="flex flex-col gap-2 mt-4">
                  {isGuestUser && (
                    <CheckboxInputStandalone
                      name="terms"
                      checked={terms}
                      onChange={onTermsChange}
                      error={termsError}
                      label={
                        <span className="text-body-8">
                          I agree to {subBrand.name}&apos;s{' '}
                          <a
                            onClick={stopPropagation}
                            target="_blank"
                            rel="noreferrer"
                            className="type-body-7-underline hover:no-underline"
                            href="/terms"
                          >
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a
                            onClick={stopPropagation}
                            target="_blank"
                            rel="noreferrer"
                            className="type-body-7-underline hover:no-underline"
                            href="/privacy"
                          >
                            Privacy Policy
                          </a>
                          .
                        </span>
                      }
                      className="!items-start"
                    />
                  )}
                  {showOversizeItemCheckbox && (
                    <CheckboxInputStandalone
                      name="oversize"
                      checked={oversizeItem}
                      onChange={onOversizeItemChange}
                      label={
                        <span className="text-body-8">
                          I certify that each item does not exceed 60 inches in
                          length, 130 inches in length plus girth, or 70 lbs. If
                          an item exceeds these limits, I may incur additional
                          charges up to $1,060. I agree that {subBrand.name} may
                          charge my provided payment method for any additional
                          charges.{' '}
                          <a
                            className="type-body-7-underline hover:no-underline"
                            onClick={stopPropagation}
                            target="_blank"
                            rel="noreferrer"
                            href="https://shipsticks-ui-sb.vercel.app"
                          >
                            Click here
                          </a>{' '}
                          for details on metric calculations and surcharge
                          amounts.
                        </span>
                      }
                      className="!items-start"
                    />
                  )}
                </div>
                {onCTAClick && (
                  <Button
                    type="submit"
                    className="w-full mt-4"
                    disabled={ctaDisabled || showLoadingIcon}
                  >
                    {showLoadingIcon ? (
                      <Icon icon="loading" className="animate-spin h-8 w-8" />
                    ) : (
                      ctaLabel
                    )}
                  </Button>
                )}
                {revertChanges && (
                  <div className="text-center mt-4">
                    <Link className="sm-max:!text-[12px]" {...revertChanges} />
                  </div>
                )}
                {showNoReceiptCheckbox && (
                  <div className="mt-4">
                    <CheckboxInputStandalone
                      name="no-receipt"
                      checked={noReceiptChecked}
                      onChange={onNoReceiptChange}
                      label={
                        <span className="text-body-8">No receipt order</span>
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              onCTAClick && (
                <Button
                  type="submit"
                  className="w-full mt-4"
                  disabled={ctaDisabled || showLoadingIcon}
                >
                  {showLoadingIcon ? (
                    <Icon icon="loading" className="animate-spin h-8 w-8" />
                  ) : (
                    ctaLabel
                  )}
                </Button>
              )
            )}
          </div>
        </div>
        {/* Render full mobile drawer when not expanded */}
        {!expanded && (
          <div
            className={clsx(
              'lg:hidden fixed bottom-0 left-0 w-screen transition-all transform z-30',
              expanded && 'translate-y-full invisible opacity-0',
            )}
          >
            <div className="sticky boottom-0 flex justify-between items-center pt-4 px-6 pb-5 bg-neutral-200">
              <button
                type="button"
                className="flex items-center gap-2"
                onClick={() => setExpanded(true)}
              >
                <Icon
                  icon="chevron-up"
                  className={clsx(
                    'w-6 h-6 transition-transform duration-300',
                    expanded && 'rotate-180',
                  )}
                />
                {stepperProps.current > 1 && (
                  <>
                    <span className="type-body-5">
                      Total
                      {totalPriceFormatted[0] === '$'
                        ? `(${selectedCurrency})`
                        : ''}
                      :
                    </span>
                    <span className="text-theme-accessibility text-right">
                      <span aria-label="TotalPrice" className="type-body-1">
                        {totalPriceFormatted}
                      </span>
                    </span>
                  </>
                )}
              </button>
              <Button
                type="submit"
                className="type-body-7 min-w-[90px] !min-h-[44px] md:min-w-80"
                disabled={ctaDisabled || showLoadingIcon}
              >
                {showLoadingIcon ? (
                  <Icon icon="loading" className="animate-spin h-8 w-8" />
                ) : (
                  <>
                    <span className="sm-max:hidden">{ctaLabel}</span>
                    <span className="md:hidden">{mobileCtaLabel}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Always-visible chevron icon when the drawer is slid out */}
        {expanded && (
          <div className="lg:hidden fixed bottom-0 left-0 w-screen z-30">
            <div className="pt-3 pl-6 pb-7 bg-transparent">
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="flex items-center gap-2"
              >
                <Icon
                  icon="chevron-up"
                  className={clsx(
                    'w-6 h-6 transition-transform duration-300',
                    expanded && 'rotate-180',
                  )}
                />
              </button>
            </div>
          </div>
        )}
      </form>
      <CurrencySelector />
    </RemoveScroll>
  )
}
